import { methodPolicies, serviceGuardTypes } from './policies';
/**
 * 서비스 이름을 저장하기 위한 Symbol
 * minification으로부터 보호되며, 런타임에 안전하게 접근 가능
 */
const SERVICE_NAME_SYMBOL = Symbol.for('@template/api:serviceName');
/**
 * 호출 차단 시 일관된 에러 반환
 * @param message - 에러 메시지
 * @param code - 에러 코드
 */
function rejectBlocked(message, code) {
    const error = new Error(message);
    error.code = code;
    return Promise.reject(error);
}
function toBoolean(value) {
    if (typeof value === 'boolean')
        return value;
    if (value && typeof value === 'object' && 'value' in value) {
        const v = value.value;
        if (typeof v === 'boolean')
            return v;
    }
    return !!value;
}
/**
 * 가드 타입에 따른 사전 체크 함수 생성
 * @param type - 가드 타입
 * @param config - 가드 설정
 */
function createPrecheck(type, config) {
    if (type === 'public') {
        return () => { };
    }
    if (type === 'login') {
        return async (...args) => {
            const loginResult = await Promise.resolve(config.checkLogin());
            const isLoggedIn = toBoolean(loginResult);
            if (!isLoggedIn) {
                config.onBlocked?.('LOGIN_REQUIRED');
                return rejectBlocked('로그인이 필요합니다', 'LOGIN_REQUIRED');
            }
        };
    }
    // type === 'account'
    return async (...args) => {
        const loginResult = await Promise.resolve(config.checkLogin());
        const isLoggedIn = toBoolean(loginResult);
        if (!isLoggedIn) {
            config.onBlocked?.('LOGIN_REQUIRED');
            return rejectBlocked('로그인이 필요합니다', 'LOGIN_REQUIRED');
        }
        const accountResult = await Promise.resolve(config.checkAccount());
        const hasAccount = toBoolean(accountResult);
        if (!hasAccount) {
            config.onBlocked?.('ACCOUNT_REQUIRED');
            return rejectBlocked('선택된 계좌가 없습니다', 'ACCOUNT_REQUIRED');
        }
    };
}
/**
 * 서비스 메서드에 사전 체크를 적용하는 래퍼 (서비스 단위)
 * @param service - 원본 서비스 인스턴스
 * @param guardType - 적용할 가드 타입
 * @param config - 가드 설정
 * @returns 가드가 적용된 서비스 인스턴스
 */
export function withGuard(service, guardType, config) {
    const precheck = createPrecheck(guardType, config);
    const handler = {
        get(target, prop, receiver) {
            const value = Reflect.get(target, prop, receiver);
            if (typeof value !== 'function')
                return value;
            return async function wrappedMethod(...args) {
                await precheck(...args);
                return value.apply(target, args);
            };
        },
    };
    return new Proxy(service, handler);
}
/**
 * 메서드별 정책에 따라 사전 체크를 적용하는 래퍼 (메서드 단위)
 * @param service - 원본 서비스 인스턴스
 * @param policy - 메서드명 -> 가드 타입 매핑
 * @param config - 가드 설정
 * @param defaultGuard - 폴백 가드 (지정되지 않은 메서드에 적용), 기본값 'account'
 * @returns 가드가 적용된 서비스 인스턴스
 */
export function withMethodPolicy(service, policy, config, defaultGuard = 'account') {
    const handler = {
        get(target, prop, receiver) {
            const value = Reflect.get(target, prop, receiver);
            if (typeof value !== 'function')
                return value;
            const methodName = String(prop);
            const guardType = policy[methodName] ?? defaultGuard;
            const precheck = createPrecheck(guardType, config);
            return async function wrappedMethod(...args) {
                await precheck(...args);
                return value.apply(target, args);
            };
        },
    };
    return new Proxy(service, handler);
}
/**
 * 서비스에 가드 자동 적용
 * policies.ts에 정의된 정책을 자동으로 찾아서 적용합니다.
 *
 * - 메서드별 정책이 있는 경우: methodPolicies에서 찾아 적용
 * - 서비스 전체 정책이 있는 경우: serviceGuardTypes에서 찾아 적용
 * - 둘 다 없는 경우: 기본값 'account'로 처리 (로그인+계좌 필요)
 *
 * @param service - 가드를 적용할 서비스 인스턴스
 * @param config - 가드 설정
 * @param serviceName - 서비스 이름 (선택사항, minification 방지를 위해 명시적으로 전달 권장)
 * @returns 가드가 적용된 서비스 인스턴스
 */
export function applyGuard(service, config, serviceName) {
    // minification으로 인한 constructor.name 변경 방지를 위해 Symbol 또는 명시적 이름 사용
    const name = serviceName || service[SERVICE_NAME_SYMBOL] || service.constructor.name;
    // 메서드별 정책이 있는 경우
    const methodPolicy = methodPolicies[name];
    if (methodPolicy) {
        // 기본값을 'account'로 적용: 명시되지 않은 메서드는 로그인+계좌 필요
        return withMethodPolicy(service, methodPolicy, config, 'account');
    }
    // 서비스 전체 정책이 있는 경우
    const guardType = serviceGuardTypes[name];
    if (guardType) {
        return withGuard(service, guardType, config);
    }
    // 정책이 없는 경우 기본값 'account' 적용
    return withGuard(service, 'account', config);
}
//# sourceMappingURL=guards.js.map